import React, { useState, useMemo } from 'react';
import { Table, Empty, Modal, Button } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import TableToolbar from './TableToolbar';
import Stats from '../Stats';
import { exportToExcel, printTable } from './exportUtils';
import './DTable.css';

const DTable = ({
  pageName,
  stats,
  columns = [],
  dataSource = [],
  loading = false,
  title,
  rowKey = 'id',
  searchable = true,
  selectable = false,
  printable = true,
  exportable = true,
  onRefresh,
  filename = 'export',
  pagination: paginationProp,
  scroll,
  addwidth = 600,
  onChange,
  AddComponent,
  EditComponent,
  addModalTitle = "Yangi qo'shish",
  editModalTitle = 'Tahrirlash',
  onDelete,
  sortable = true,
  ...rest
}) => {
  const [searchText, setSearchText] = useState('');
  const [tableSize, setTableSize] = useState('middle');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const isAddValid = typeof AddComponent === 'function';
  const isEditValid = typeof EditComponent === 'function';

  const [visibleColumns] = useState(() =>
    Object.fromEntries(
      columns
        .filter((col) => col.dataIndex || col.key)
        .map((col) => [String(col.key || col.dataIndex), true])
    )
  );
  const [visibleCols, setVisibleCols] = useState(visibleColumns);

  const handleToggleColumn = (key) =>
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));

  const filteredData = useMemo(() => {
    if (!searchText.trim()) return dataSource;
    const q = searchText.toLowerCase();
    return dataSource.filter((row) =>
      columns.some((col) => {
        if (!col.dataIndex) return false;
        return String(row[col.dataIndex] ?? '').toLowerCase().includes(q);
      })
    );
  }, [dataSource, searchText, columns]);

  const activeColumns = useMemo(
    () =>
      columns
        .filter((col) => {
          if (!col.dataIndex && !col.key) return true;
          return visibleCols[String(col.key || col.dataIndex)] !== false;
        })
        .map((col) => {
          if (!sortable || !col.dataIndex || col.sorter !== undefined) return col;
          return {
            ...col,
            sorter: (a, b) => {
              const valA = a[col.dataIndex];
              const valB = b[col.dataIndex];
              if (valA == null && valB == null) return 0;
              if (valA == null) return -1;
              if (valB == null) return 1;
              if (typeof valA === 'number' && typeof valB === 'number') return valA - valB;
              return String(valA).localeCompare(String(valB));
            },
          };
        }),
    [columns, visibleCols, sortable]
  );

  const finalColumns = useMemo(() => {
    const hasEdit = !!EditComponent;
    const hasDelete = typeof onDelete === 'function';
    if (!hasEdit && !hasDelete) return activeColumns;
    const actionCol = {
      key: '__actions',
      title: '',
      fixed: 'right',
      width: hasEdit && hasDelete ? 90 : 48,
      exportable: false,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 4 }}>
          {hasEdit && (
            <Button
              type="primary"
              size="medium"
              icon={<EditOutlined />}
              onClick={() => { setEditData(record); setEditOpen(true); }}
            />
          )}
          {hasDelete && (
            <Button
              type="primary"
              size="medium"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: "O'chirishni tasdiqlaysizmi?",
                  content: "Bu amalni ortga qaytarib bo'lmaydi.",
                  okText: "O'chirish",
                  okType: 'danger',
                  cancelText: 'Bekor qilish',
                  onOk: () => onDelete(record),
                });
              }}
            />
          )}
        </div>
      ),
    };
    return [...activeColumns, actionCol];
  }, [activeColumns, EditComponent, onDelete]);

  const rowSelection = selectable
    ? {
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
      preserveSelectedRowKeys: true,
    }
    : undefined;

  const pagination =
    paginationProp === false
      ? false
      : {
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (total, range) =>
          `${range[0]}–${range[1]} / jami ${total} ta`,
        defaultPageSize: 10,
        ...paginationProp,
      };

  return (
    <div className="dtable-wrapper">
      {stats?.length > 0 && (
        <Stats items={stats} style={{ marginBottom: 16 }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>{pageName}</h3>
        <TableToolbar
          title={title}
          searchable={searchable}
          searchText={searchText}
          onSearch={setSearchText}
          columns={columns}
          visibleColumns={visibleCols}
          onToggleColumn={handleToggleColumn}
          tableSize={tableSize}
          onSizeChange={setTableSize}
          printable={printable}
          exportable={exportable}
          onRefresh={onRefresh}
          loading={loading}
          onPrint={() => printTable(title, activeColumns, filteredData)}
          onExport={() => exportToExcel(activeColumns, filteredData, filename)}
          selectedCount={selectedRowKeys.length}
          onAdd={isAddValid ? () => setAddOpen(true) : undefined}
        />
      </div>


      {isAddValid && (
        <Modal
          open={addOpen}
          title={addModalTitle}
          onCancel={() => setAddOpen(false)}
          footer={null}
          destroyOnHidden
          width={addwidth}
        >
          <AddComponent
            onClose={() => setAddOpen(false)}
            onSuccess={() => { setAddOpen(false); onRefresh?.(); }}
          />
        </Modal>
      )}

      {isEditValid && (
        <Modal
          open={editOpen}
          title={editModalTitle}
          onCancel={() => { setEditOpen(false); setEditData(null); }}
          footer={null}
          destroyOnHidden
          width={addwidth}
        >
          <EditComponent
            data={editData}
            onClose={() => { setEditOpen(false); setEditData(null); }}
            onSuccess={() => { setEditOpen(false); setEditData(null); onRefresh?.(); }}
          />
        </Modal>
      )}

      <Table
        rowKey={rowKey}
        columns={finalColumns}
        dataSource={filteredData}
        loading={loading}
        size={tableSize}
        rowSelection={rowSelection}
        sortDirections={['ascend', 'descend', 'ascend']}
        pagination={pagination}
        onChange={onChange}
        scroll={scroll ?? { x: 'max-content' }}
        locale={{
          emptyText: <Empty description="Ma'lumot topilmadi" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
          filterConfirm: 'OK',
          filterReset: 'Tozalash',
          filterTitle: 'Filter',
          filterEmptyText: 'Filter yo\'q',
          filterSearchPlaceholder: 'Filtrlarda qidirish',
          selectAll: 'Hammasini tanlash',
          selectNone: 'Tanlashni bekor qilish',
          selectInvert: 'Teskari tanlash',
          selectionAll: 'Barchani tanlash',
          sortTitle: 'Saralash',
          expand: 'Kengaytirish',
          collapse: 'Yig\'ish',
          triggerDesc: 'Kamayish tartibida',
          triggerAsc: 'O\'sish tartibida',
          cancelSort: 'Saralashni bekor qilish',
        }}
        {...rest}
      />
    </div>
  );
};

export default DTable;
